/**
 * Articles API service — typed wrapper around apiFetch for all article operations.
 */
import { apiFetch } from "@/lib/jwt-auth-client";

const BASE = "/core/content-studio";

async function post<T = Record<string, unknown>>(
  path: string,
  body: Record<string, unknown>,
): Promise<T> {
  const res = await apiFetch(`${BASE}/${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || res.statusText);
  }
  return res.json();
}

async function get<T = Record<string, unknown>>(
  path: string,
  params?: Record<string, string>,
): Promise<T> {
  const url = params ? `${BASE}/${path}?${new URLSearchParams(params)}` : `${BASE}/${path}`;
  const res = await apiFetch(url);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || res.statusText);
  }
  return res.json();
}

export interface ArticleListParams {
  workspaceId: string;
  page?: number;
  pageSize?: number;
  search?: string;
  source?: string;
  statusGroup?: string;
  isRead?: string;
  isStarred?: string;
  ordering?: string;
  ids?: string;
  entryType?: string;
  mode?: string;
}

export interface ArticleListItem {
  id: string;
  name: string;
  content_md?: string;
  metadata: Record<string, any>;
  created_at: string | null;
  updated_at: string | null;
}

export interface ArticleListResponse {
  articles: ArticleListItem[];
  total: number;
  page: number;
  page_size: number;
}

export const articlesService = {
  // ── List & Fetch ───────────────────────────────────────────
  list(params: ArticleListParams): Promise<ArticleListResponse> {
    const p: Record<string, string> = { workspaceId: params.workspaceId };
    if (params.page) p.page = String(params.page);
    if (params.pageSize) p.page_size = String(params.pageSize);
    if (params.search) p.search = params.search;
    if (params.source) p.source = params.source;
    if (params.statusGroup) p.status_group = params.statusGroup;
    if (params.isRead) p.is_read = params.isRead;
    if (params.isStarred) p.is_starred = params.isStarred;
    if (params.ordering) p.ordering = params.ordering;
    if (params.ids) p.ids = params.ids;
    if (params.entryType) p.entry_type = params.entryType;
    if (params.mode) p.mode = params.mode;
    return get<ArticleListResponse>("article/list", p);
  },

  sourceOptions(workspaceId: string) {
    return get<{ sources: { id: string; name: string }[] }>("article/source-options", {
      workspaceId,
    });
  },

  // ── Single Article Actions ─────────────────────────────────
  extract(articleArtifactId: string) {
    return post("article/extract", { articleArtifactId });
  },

  queue(articleArtifactId: string) {
    return post("article/queue", { articleArtifactId });
  },

  process(articleArtifactId: string, flowArtifactId?: string) {
    return post("article/process", { articleArtifactId, flowArtifactId });
  },

  run(articleArtifactId: string, flowArtifactId?: string) {
    return post("article/run", { articleArtifactId, flowArtifactId });
  },

  publish(articleArtifactId: string) {
    return post("article/publish", { articleArtifactId });
  },

  skip(articleArtifactId: string) {
    return post("article/skip", { articleArtifactId });
  },

  retry(articleArtifactId: string) {
    return post("article/retry", { articleArtifactId });
  },

  cancel(articleArtifactId: string) {
    return post("article/cancel", { articleArtifactId });
  },

  // ── Mutations ──────────────────────────────────────────────
  update(
    articleArtifactId: string,
    data: { title?: string; content_md?: string; wp_metadata?: Record<string, any> },
  ) {
    return post("article/update", {
      articleArtifactId,
      title: data.title,
      contentMd: data.content_md,
      wpMetadata: data.wp_metadata,
    });
  },

  delete(articleArtifactId: string) {
    return post("article/bulk-action", { ids: [articleArtifactId], action: "delete" });
  },

  // ── Quick Add ──────────────────────────────────────────────
  quickAdd(workspaceId: string, url: string, flowArtifactId?: string) {
    return post("article/quick-add", { workspaceId, url, flowArtifactId });
  },

  // ── Read / Star ────────────────────────────────────────────
  markRead(articleArtifactId: string) {
    return post("article/mark-read", { articleArtifactId });
  },

  markUnread(articleArtifactId: string) {
    return post("article/mark-unread", { articleArtifactId });
  },

  toggleStar(articleArtifactId: string) {
    return post("article/toggle-star", { articleArtifactId });
  },

  markAllRead(workspaceId: string, sourceArtifactId?: string) {
    return post("article/mark-all-read", { workspaceId, sourceArtifactId });
  },

  unreadCounts(workspaceId: string) {
    return get<{ total: number; by_source: { id: string; name: string; count: number }[] }>(
      "article/unread-counts",
      { workspaceId },
    );
  },

  // ── Media ──────────────────────────────────────────────────
  async uploadImage(
    articleArtifactId: string,
    file: File,
  ): Promise<{ url: string; media_id: number }> {
    const formData = new FormData();
    formData.append("articleArtifactId", articleArtifactId);
    formData.append("file", file);
    const res = await apiFetch(`${BASE}/article/upload-image`, {
      method: "POST",
      body: formData,
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || res.statusText);
    return res.json();
  },

  async replaceFeaturedImage(
    articleArtifactId: string,
    file: File,
  ): Promise<{ url: string; media_id: number }> {
    const formData = new FormData();
    formData.append("articleArtifactId", articleArtifactId);
    formData.append("file", file);
    const res = await apiFetch(`${BASE}/article/replace-featured-image`, {
      method: "POST",
      body: formData,
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || res.statusText);
    return res.json();
  },

  // ── WordPress ──────────────────────────────────────────────
  updateWP(articleArtifactId: string) {
    return post("article/update-wp", { articleArtifactId });
  },

  getPublishInfo(articleArtifactId: string) {
    return get("article/publish-info", { articleArtifactId });
  },

  // ── Bulk ───────────────────────────────────────────────────
  bulkAction(ids: string[], action: string) {
    return post("article/bulk-action", { ids, action });
  },

  // ── Pipeline ───────────────────────────────────────────────
  pipeline(articleArtifactId: string) {
    return post("article/pipeline", { articleArtifactId });
  },
};
